import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Button,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Description as ContractsIcon,
  Assignment as InstancesIcon,
  AttachMoney as CostsIcon,
  Category as GroupsIcon,
  Assessment as ReportsIcon,
  Groups as TeamsIcon,
  Contacts as ContactsIcon,
  RequestQuote as ProposalsIcon,
  Logout as LogoutIcon,
  TrendingUp as LeadsIcon,
  AccountBalanceWallet as BankingIcon,
  PriceChange as ClientBalancesIcon,
  Assessment as LedgerIcon,
  RequestQuote as ARIcon,
  AccountBalance as APIcon,
  SupervisorAccount as AppUsersIcon,
  Settings as AppConfigIcon,
  Autorenew as SubscriptionsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { useTheme } from '@mui/material/styles';

const drawerWidth = 280;
const DRAWER_STATE_KEY = 'layout.drawerOpen';

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
];

// 📂 CUSTOMERS Section
const customersItems = [
  { 
    text: 'Leads', 
    icon: <LeadsIcon />, 
    path: '/leads',
    description: 'Lightweight prospect tracking with conversion logic'
  },
  { 
    text: 'Clients', 
    icon: <PeopleIcon />, 
    path: '/clients',
    description: 'Active, converted customers with relationships and billing'
  },
  { 
    text: 'Contacts', 
    icon: <ContactsIcon />, 
    path: '/contacts',
    description: 'People linked to clients; deletable unless bound'
  },
];

// 🏢 BUSINESS Section
const businessItems = [
  {
    text: 'Tasks',
    icon: <InstancesIcon />,
    path: '/tasks',
    description: 'Kanban board for internal work items'
  },
  {
    text: 'Entities',
    icon: <TeamsIcon />,
    path: '/entities',
    description: 'Every payable/receivable counterparty (internal + external)'
  },
  { 
    text: 'Services', 
    icon: <CostsIcon />, 
    path: '/costs',
    description: 'Building blocks of proposals and contracts'
  },
  { 
    text: 'Proposals', 
    icon: <ProposalsIcon />, 
    path: '/proposals',
    description: 'Offers to clients based on cost combinations'
  },
  { 
    text: 'Contracts', 
    icon: <ContractsIcon />, 
    path: '/contracts',
    description: 'Immutable binding agreements triggered by proposal acceptance'
  },
  {
    text: 'Subscriptions',
    icon: <SubscriptionsIcon />,
    path: '/subscriptions',
    description: 'Client subscriptions, terms and renewal dates'
  },
 
];

// 💰 FINANCE Section
const financeItems = [
  {
    text: 'Client Balances',
    icon: <ClientBalancesIcon />,
    path: '/client-balances',
    description: 'View client funds and make signed balance adjustments'
  },
  { 
    text: 'Bank Management', 
    icon: <BankingIcon />, 
    path: '/bank-management',
    description: 'Ingest bank activity and explain per UMA rules'
  },
  { 
    text: 'Accounts Receivable', 
    icon: <ARIcon />, 
    path: '/accounts-receivable',
    description: 'Apply client funds and close invoices'
  },
  { 
    text: 'Accounts Payable', 
    icon: <APIcon />, 
    path: '/accounts-payable',
    description: 'Projection of amounts owed to entities from the ledger'
  },
  { 
    text: 'Ledger', 
    icon: <LedgerIcon />, 
    path: '/ledger',
    description: 'Append-only source of truth for Direct accounts'
  },
];

// 🛠️ CONFIGURATION Section (Super Admin Only)
const configurationItems = [
  { 
    text: 'App Users', 
    icon: <AppUsersIcon />, 
    path: '/app-users',
    description: 'System user and permission management'
  },
  { 
    text: 'App Config', 
    icon: <AppConfigIcon />, 
    path: '/app-config',
    description: 'All global keys, settings, branding, and system rules'
  },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isSuperAdmin, canViewFinance, userProfile, currentUser } = useAuth();
  const { getBrandingConfig } = useConfig();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = useState(() => {
    try {
      if (typeof window === 'undefined') return isDesktop;
      if (!isDesktop) return false; // never auto-open on mobile
      const saved = localStorage.getItem(DRAWER_STATE_KEY);
      if (saved === 'true') return true;
      if (saved === 'false') return false;
      return true; // default open on desktop
    } catch (e) {
      return isDesktop;
    }
  });

  // Rehydrate on breakpoint changes (desktop vs mobile)
  useEffect(() => {
    try {
      if (isDesktop) {
        const saved = localStorage.getItem(DRAWER_STATE_KEY);
        if (saved === 'true' || saved === 'false') {
          setDrawerOpen(saved === 'true');
        } else {
          setDrawerOpen(true);
        }
      } else {
        setDrawerOpen(false);
      }
    } catch (e) {
      setDrawerOpen(isDesktop);
    }
  }, [isDesktop]);

  // Persist when state changes (desktop only)
  useEffect(() => {
    try {
      if (isDesktop) {
        localStorage.setItem(DRAWER_STATE_KEY, String(drawerOpen));
      }
    } catch (e) {
      // ignore storage write errors
    }
  }, [drawerOpen, isDesktop]);

  // Get dynamic branding
  const branding = getBrandingConfig();
  const publicUrl = process.env.PUBLIC_URL || '';
  // App icon is fixed (not taken from App Configuration branding)
  const headerLogoSrc = `${publicUrl}/app-icon.png`;
  const userAvatarSrc = userProfile?.avatarUrl || currentUser?.photoURL || '';

  // Compute display name and avatar initial with robust fallbacks
  const fullName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ').trim();
  const fallbackName = currentUser?.displayName || currentUser?.email?.split('@')[0];
  const displayName = fullName || fallbackName || 'User';
  const avatarInitial = (displayName?.charAt(0) || 'U').toUpperCase();

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const MenuItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    
    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            navigate(item.path);
            if (!isDesktop) setDrawerOpen(false);
          }}
          sx={{
            backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
            borderRight: isActive ? '3px solid #1976d2' : 'none',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText 
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: isActive ? 'bold' : 'normal',
              color: isActive ? 'primary.main' : 'inherit',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const SectionHeader = ({ emoji, title }) => (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography 
        variant="subtitle2" 
        color="text.secondary" 
        sx={{ 
          fontWeight: 'bold',
          fontSize: '0.75rem',
          letterSpacing: '0.5px'
        }}
      >
        {emoji} {title}
      </Typography>
    </Box>
  );

  const drawer = (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Navigation */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain' }}>
        {/* Main Dashboard */}
        <List>
          {menuItems.map((item) => (
            <MenuItem key={item.text} item={item} />
          ))}
        </List>

        <Divider />

        {/* 📂 CUSTOMERS Section */}
        <SectionHeader emoji="📂" title="CUSTOMERS" />
        <List sx={{ pt: 0 }}>
          {customersItems.map((item) => (
            <MenuItem key={item.text} item={item} />
          ))}
        </List>

        <Divider />

        {/* 🏢 BUSINESS Section */}
        <SectionHeader emoji="🏢" title="BUSINESS" />
        <List sx={{ pt: 0 }}>
          {businessItems.map((item) => (
            <MenuItem key={item.text} item={item} />
          ))}
        </List>

        {/* 💰 FINANCE Section (hidden from Members) */}
        {canViewFinance() && (
          <>
            <Divider />
            <SectionHeader emoji="💰" title="FINANCE" />
            <List sx={{ pt: 0 }}>
              {financeItems.map((item) => (
                <MenuItem key={item.text} item={item} />
              ))}
            </List>
          </>
        )}

        {/* 🛠️ CONFIGURATION Section (Super Admin Only) */}
        {isSuperAdmin() && (
          <>
            <Divider />
            <SectionHeader emoji="🛠️" title="CONFIGURATION" />
          
            <List sx={{ pt: 0 }}>
              {configurationItems.map((item) => (
                <MenuItem key={item.text} item={item} />
              ))}
            </List>
          </>
        )}
      </Box>

      {/* User Profile & Logout */}
      <Box sx={{ p: 2, flexShrink: 0, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={userAvatarSrc || undefined}
            alt={displayName}
            sx={{
              mr: 2,
              bgcolor: userAvatarSrc ? 'transparent' : 'primary.main',
              color: userAvatarSrc ? 'inherit' : '#fff',
              width: 40,
              height: 40,
              border: userAvatarSrc ? '1px solid rgba(0,0,0,0.05)' : 'none',
              fontWeight: 'bold',
              '& img': {
                objectFit: 'cover',
              },
            }}
          >
            {!userAvatarSrc && (avatarInitial || 'U')}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {{ SA: 'Super Admin', Developer: 'Developer', Member: 'Member' }[userProfile?.role] || 'Partner'}
            </Typography>
          </Box>
        </Box>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          size="small"
        >
          Logout
        </Button>

        <p style={{ fontSize: '0.7rem', color: 'gray', textAlign: 'center' }}>v11.1.0 GA</p>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          // Keep header full-width; drawer will render underneath it
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          {/* Dynamic App Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            {/* App icon tile: same height as the two text lines; white so it reads on any theme colour */}
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: '#fff',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={headerLogoSrc}
                alt={branding.companyName || 'UCENA'}
                sx={{
                  width: 22,
                  height: 22,
                  display: 'block',
                  // Optical centring: the mark's weight sits up-right, so nudge it down-left
                  transform: 'translate(-8%, 8%)',
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap component="div" sx={{ lineHeight: 1.1 }}>
                {branding.companyName || 'Service & Client Management System'}
              </Typography>
              {branding.tagline && (
                <Typography variant="caption" noWrap component="div" sx={{ opacity: 0.8, fontSize: '0.7rem', lineHeight: 1.25, mt: 0.25 }}>
                  {branding.tagline}
                </Typography>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: isDesktop ? (drawerOpen ? drawerWidth : 0) : 0,
          flexShrink: 0,
        }}
        aria-label="navigation"
      >
        <Drawer
          variant={isDesktop ? 'persistent' : 'temporary'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={
            isDesktop
              ? undefined
              : {
                  keepMounted: true,
                }
          }
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: '64px',
              height: 'calc(100% - 64px)',
              overflow: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: 3,
          mt: '64px',
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // No additional left margin here; space is already reserved by the nav Box width
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
